import { store } from "../store/store";

/**
 * Formats a phone number to (999) 999 9999 format
 * @param {string|number} phone - The phone number to format
 * @returns {string} Formatted phone number or original value if invalid
 */
export const formatPhone = (phone) => {
  if (!phone) return '';

  // Remove all non-digit characters
  const digits = String(phone).replace(/\D/g, '');

  // Handle 10-digit phone numbers
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }

  // Handle 11-digit numbers (with country code 1)
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }

  // Return original if doesn't match expected format
  return String(phone);
};

export const formatDate = (d) => {
  if (!d) {
    return "";
  }
  const date = new Date(d);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

/**
 * Formats a date to MM/DD/YY format in CST timezone
 * @param {string|Date} d - The date to format
 * @returns {string} Formatted date in MM/DD/YY CST or empty string if invalid
 */
export const formatDateCST = (d) => {
  if (!d) {
    return "";
  }

  const date = new Date(d);
  if (isNaN(date.getTime())) {
    return "";
  }

  // Convert to CST (Central Standard Time - UTC-6)
  const cstDate = new Date(date.toLocaleString("en-US", { timeZone: "America/Chicago" }));

  const month = (cstDate.getMonth() + 1).toString().padStart(2, "0");
  const day = cstDate.getDate().toString().padStart(2, "0");
  const year = cstDate.getFullYear().toString().slice(-2); // Get last 2 digits of year

  return `${month}/${day}/${year}`;
};

/**
 * Formats a date to HH:MM:SS format in CST timezone
 * @param {string|Date} d - The date to format
 * @returns {string} Formatted time in HH:MM:SS CST or empty string if invalid
 */
export const formatTimeCST = (d) => {
  if (!d) {
    return "";
  }

  const date = new Date(d);
  if (isNaN(date.getTime())) {
    return "";
  }

  // Convert to CST (Central Standard Time - UTC-6)
  const cstDate = new Date(date.toLocaleString("en-US", { timeZone: "America/Chicago" }));

  const hours = cstDate.getHours().toString().padStart(2, "0");
  const minutes = cstDate.getMinutes().toString().padStart(2, "0");
  const seconds = cstDate.getSeconds().toString().padStart(2, "0");

  return `${hours}:${minutes}:${seconds}`;
};

export const mapToOption = (arr = [], key = null, valKey = null) => {
  if (!Array.isArray(arr)) {
    return [{ value: "", text: "--Choose an option--" }];
  }

  if (key) {
    const optionArr = arr.map((i) => ({
      value: i[valKey || key] || "",
      text: i[key] || "",
    }));
    return [{ value: "", text: "--Choose an option--" }, ...optionArr];
  }

  return [
    { value: "", text: "--Choose an option--" },
    ...arr.map((i) => ({ value: i || "", text: i || "" })),
  ];
};

export const getUserInfoDetails = () => {
  const data = store.getState();
  const userInfo = data.user.userInfo;

  // Handle case where userInfo is null (e.g., CORS error prevented API call)
  if (!userInfo) {
    return {
      companyId: null,
      clientId: null,
    };
  }

  return {
    companyId: userInfo.company,
    clientId: userInfo.client,
    ...userInfo,
  };
};
